import { useState, type ReactNode } from 'react';
import { PageShell, Section } from '~/components/layout';
import { ButtonLink, Grid, Icon } from '~/components/ui';
import { CtaBanner } from './CtaBanner';
import { CurrencyCalculator } from './CurrencyCalculator';
import { CurrencyRatesStrip } from './CurrencyRatesStrip';
import { DownloadQr } from './DownloadQr';
import { FaqAccordion } from './FaqAccordion';
import { FeatureBlock } from './FeatureBlock';
import { RateComparison } from './RateComparison';
import { StepsSection } from './StepsSection';
import { TestimonialGrid } from './TestimonialGrid';
import { TextSection } from './TextSection';
import { compareSteps } from '~/content/compare';
import { sendMoneyHeroFeatures } from '~/content/sendMoneyHome';
import type { FaqContent } from '~/content/types';
import { useContent } from '~/lib/useLanguage';
import type { ImageRef } from '~/content/types';
import type { PricingTier, ReceiveCurrency } from '~/lib/quote';

export interface SendMoneyCalculatorPageProps {
  /** Hero H1 (supports `\n` via whitespace-pre-line). */
  heroTitle: string;
  /** Initial receive currency (from route slug or send-money default). */
  initialTarget: ReceiveCurrency;
  /** Hero feature rows. Defaults to the send-money-home set. */
  heroFeatures?: typeof sendMoneyHeroFeatures;
  /**
   * Optional hero call to action, rendered under the feature rows. Corridor
   * landing pages carry one; the calculator SEO pages do not.
   */
  heroCta?: { href: string; label: string };
  /** Page-specific FAQ. Falls back to the shared default inside FaqAccordion. */
  faqContent?: FaqContent;
  /** Heading above the fee comparison. Localised pages pass their own. */
  comparisonHeading?: string;
  /**
   * Horizontal placement of the hero's two columns.
   *
   * `start` (default) is the send-money-home arrangement: the 9-column grid with
   * copy in cols 1–4 and the calculator in 5–8, which leaves the ninth column
   * empty and so sits the pair left of centre.
   *
   * `center` drops the grid for a flex row that fills the container: the copy
   * starts on the same left edge as every section heading below it and the card
   * ends on the same right edge, so the hero reads as part of the same wrapper
   * as the rest of the page rather than a narrower block floating inside it.
   * Same two-column layout either way.
   */
  heroAlign?: 'center' | 'start';
  /**
   * Render the shared "Send easily in 3 steps" block. Pages that supply their
   * own how-to walkthrough set this false rather than showing both.
   */
  showSteps?: boolean;
  /**
   * Slot rendered directly after the fee comparison, for sections a specific
   * landing page needs without pushing page-specific markup into this shared
   * layout.
   */
  afterComparison?: ReactNode;
  /**
   * Same, rendered before the fee comparison. Corridor pages whose brief opens
   * with a how-to use this so the walkthrough leads the page instead of sitting
   * below a fee table the brief never asked for.
   */
  beforeComparison?: ReactNode;
  /**
   * Image for the left half of the "safe space for your money" band, which is
   * otherwise empty. Corridor pages pass one; `/send-money-home` and the
   * calculator SEO pages keep the plain copy-only treatment.
   */
  safeSpaceImage?: ImageRef;
}

/**
 * Shared send-money / calculator landing layout: hero + FX calculator, rates
 * strip, steps, fee comparison, download, FAQ, CTA. Pair SEO pages and
 * `/send-money-home` both render this so FX UI stays in one place.
 */
export function SendMoneyCalculatorPage({
  heroTitle,
  initialTarget,
  heroFeatures = sendMoneyHeroFeatures,
  heroCta,
  faqContent,
  afterComparison,
  heroAlign = 'start',
  showSteps = true,
  comparisonHeading = 'Compare fees',
  beforeComparison,
  safeSpaceImage,
}: SendMoneyCalculatorPageProps) {
  // Currency pair / amount / plan are owned here so the rates strip, the
  // calculator, and the comparison table all stay in sync: clicking a strip pair
  // or changing the plan updates the calculator and the table live.
  const [base, setBase] = useState('USD');
  const [target, setTarget] = useState<ReceiveCurrency>(initialTarget);
  const [amount, setAmount] = useState('1000');
  const [plan, setPlan] = useState<PricingTier>('PRO');

  // Shared sections below the hero come from the active language's bundle, so an
  // Arabic route does not render Arabic copy followed by English testimonials
  // and safe-space text. `CtaBanner` and `TestimonialGrid` already resolve their
  // own content this way.
  const { shared } = useContent();

  const calculator = (
    <CurrencyCalculator
      // The entrance animation belongs to the homepage hero, which choreographs
      // it against its own load sequence. Here the card is the first thing in
      // view, so animating it only delays the rate the visitor came to read.
      animateIn={false}
      base={base}
      target={target}
      amount={amount}
      plan={plan}
      onBaseChange={setBase}
      onTargetChange={setTarget}
      onAmountChange={setAmount}
      onPlanChange={setPlan}
    />
  );

  const featureRows = heroFeatures.map((feature) => (
    <li key={feature.title} className="flex items-start gap-5">
      <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-grey-100 text-ink">
        <Icon name={feature.icon} className="size-6" />
      </span>
      {/* Title/description styling + gap match FeatureBlock's rows below. */}
      <div className="flex flex-col gap-5">
        <h3 className="text-body font-semibold">{feature.title}</h3>
        <p className="text-body text-grey-900">{feature.description}</p>
      </div>
    </li>
  ));

  return (
    <PageShell headerTransparentUntilScroll>
      {/* Hero: full-width grey band; copy + features beside the calculator.

          The header floats transparent over this band (PageShell pulls content up
          by the 64px header via -mt-16), so h1's distance from the top of the page
          equals this pt value. It bundles three things and avoids double-counting:
          the hero's own breathing room (56 / 80), the 64px transparent-header
          clearance, and a +16px extra top padding, minus 24px —
          pt-[112px]  = 56 + 64 + 16 - 24, md:pt-[136px] = 80 + 64 + 16 - 24. */}
      <Section className="bg-grey-50 pt-[112px] pb-16 md:pt-[136px] md:pb-24">
        {heroAlign === 'center' ? (
          /* A plain flex row rather than the 9-column grid, which places content
             in cols 1–8 of 9 and leaves the ninth empty — sitting the pair off
             centre and narrower than the sections below it.
             `justify-between` fills the Container instead, so the copy starts on
             the same left edge as every section heading further down the page and
             the card ends on the same right edge. `items-center` lines the copy
             up against the middle of the card. The copy column grows to take the
             leftover space but is capped so the text measure stays readable. */
          <div className="flex flex-col gap-12 lg:flex-row lg:items-center lg:justify-between lg:gap-20">
            <div className="flex w-full flex-1 flex-col lg:max-w-[560px]">
              <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] whitespace-pre-line text-balance md:text-h2">
                {heroTitle}
              </h1>
              {/* Invisible divider: transparent border keeps the exact vertical
                  spacing of the visible rule without drawing a line. */}
              <div className="mt-8 border-t border-transparent" />
              <ul className="mt-8 flex flex-col gap-8">{featureRows}</ul>
              {heroCta ? (
                <ButtonLink href={heroCta.href} size="lg" className="mt-10 self-start">
                  {heroCta.label}
                </ButtonLink>
              ) : null}
            </div>
            <div className="flex shrink-0 justify-center">{calculator}</div>
          </div>
        ) : (
          /* Split: copy + features on the left, calculator on the right. It sits
             in grid cols 5–8 (9-col grid) so the white card is centered on the
             6/7 gridline; on mobile it stacks and centers. */
          <Grid className="items-start gap-y-12 lg:items-stretch">
            <div className="col-span-9 flex flex-col lg:col-span-4">
              {/* On desktop the left column stretches to the calculator's height
                  (Grid lg:items-stretch); this flex-1 wrapper fills the space above
                  the QR and vertically centres the title + feature list within it.
                  On mobile it collapses to content height, so the stacked spacing is
                  unchanged. */}
              <div className="flex flex-1 flex-col lg:justify-center">
                <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] whitespace-pre-line text-balance md:text-h2">
                  {heroTitle}
                </h1>
                <div className="mt-8 border-t border-transparent" />
                <ul className="mt-8 flex flex-col gap-8">{featureRows}</ul>
                {heroCta ? (
                  <ButtonLink href={heroCta.href} size="lg" className="mt-10 self-start">
                    {heroCta.label}
                  </ButtonLink>
                ) : null}
              </div>
              {/* QR stays pinned to the bottom (aligned with the 508px calculator
                  card) with an 80px gap above it. */}
              <DownloadQr className="mt-20 self-start" />
            </div>
            {/* Extra 24px gap above the calculator on mobile only (net 72px with the
                Grid's gap-y-12); removed at md+ where the QR appears / layout goes
                side-by-side, so desktop spacing is unchanged. */}
            <div className="col-span-9 mt-6 flex justify-center md:mt-0 lg:col-start-5 lg:col-span-4">
              {calculator}
            </div>
          </Grid>
        )}
      </Section>
      <CurrencyRatesStrip onSelect={setTarget} plan={plan} />
      {/* Send easily in 3 steps: 3-card grid (reuses the /compare steps content),
          positioned directly above the Compare fees table. Extra 80px top gap
          (pt-20) between the currency-rates strip and this section. */}
      {showSteps ? (
        <div className="pt-[120px] pb-10">
          <StepsSection heading="Send easily in 3 steps" steps={compareSteps} />
        </div>
      ) : null}
      {beforeComparison}
      {/* Unified comparison table, driven by the calculator's live pair/amount/plan. */}
      <RateComparison
        heading={comparisonHeading}
        amount={Number.parseFloat(amount) || 0}
        source={base}
        target={target}
        plan={plan}
        showSavings
        onAmountChange={setAmount}
        onSourceChange={setBase}
        onTargetChange={(code) => setTarget(code as ReceiveCurrency)}
      />
      {afterComparison}
      {/* Extra 40px gap (pt-10) between the comparison table and the "Download the
          nsave app" section, consistent with how sections are spaced on this page. */}
      <div className="pt-10">
        <FeatureBlock
          content={shared.appDownloadSection}
          imagePosition="left"
          ctaSlot={<DownloadQr />}
          // Measured inert on this block today: the image is a fixed 720px and
          // the rows beside it come to ~595px, so there is no scroll range for
          // it to stick through. Kept because it is the intended behaviour and
          // starts working the moment the image shrinks or the rows grow.
          stickyImage
        />
      </div>
      <TextSection
        content={shared.safeSpaceSection}
        image={safeSpaceImage}
        sectionClassName="max-md:!my-[40px]"
      />
      <TestimonialGrid content={shared.defaultTestimonials} />
      <FaqAccordion content={faqContent} />
      <CtaBanner />
    </PageShell>
  );
}
